import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import { useAuth } from '../../contexts/AuthContext';
import {
  getFacilityTasks, createFacilityTask, updateFacilityTask, deleteFacilityTask,
  completeFacilityTask, uncompleteFacilityTask, updateFacilityTaskCompletion,
  getFacilityComplaints, resolveFacilityComplaint, finishFacilityComplaint, addComplaintComment,
  getFacilityOtherWorks, createFacilityOtherWork, updateFacilityOtherWork,
  deleteFacilityOtherWork, markFacilityOtherWorkDone,
  getFacilityWorkLog, getFMUsers,
  checkFMLeaveDay, markFMLeaveDay, unmarkFMLeaveDay, getFMLastWorkingDayPending,
  getFMonthlySummary,
} from '../../api/facility';
import {
  HiOutlineClipboardCheck, HiOutlineExclamationCircle, HiOutlineBriefcase,
  HiOutlinePlus, HiOutlinePencil, HiOutlineTrash, HiOutlineX,
  HiOutlinePhotograph, HiOutlineClock, HiOutlineUser, HiOutlineCheck,
  HiCheckCircle, HiOutlineRefresh, HiOutlineChartBar, HiOutlineCalendar,
  HiOutlineChevronDown, HiOutlineBell, HiOutlineChevronRight,
} from 'react-icons/hi';

// ─── helpers ────────────────────────────────────────────────────────────────

const PRIORITY_META = {
  low:    { label: 'Low',    cls: 'bg-gray-100 text-gray-600' },
  medium: { label: 'Medium', cls: 'bg-amber-100 text-amber-700' },
  high:   { label: 'High',   cls: 'bg-red-100 text-red-600' },
};

const STATUS_META = {
  open:        { label: 'Open',        cls: 'bg-red-100 text-red-600' },
  in_progress: { label: 'In Progress', cls: 'bg-amber-100 text-amber-700' },
  finished:    { label: 'Finished',    cls: 'bg-blue-100 text-blue-700' },
  resolved:    { label: 'Resolved',    cls: 'bg-green-100 text-green-700' },
};

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function fmtMinutes(m) {
  if (!m) return null;
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
}

// ─── shared components ───────────────────────────────────────────────────────

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}>
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? 'max-w-lg' : 'max-w-md'} max-h-[90vh] overflow-y-auto`}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100">
            <HiOutlineX className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1.5">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls = 'w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-gray-50 focus:bg-white transition-colors';

function Btn({ onClick, disabled, color = 'primary', size = 'md', children, className = '' }) {
  const colors = {
    primary: 'text-white',
    green:   'text-white',
    outline: 'border border-gray-200 text-gray-600 bg-white hover:bg-gray-50',
    danger:  'text-white',
  };
  const bgs = {
    primary: { background: 'linear-gradient(135deg,#032841,#043959)' },
    green:   { background: 'linear-gradient(135deg,#16a34a,#15803d)' },
    danger:  { background: 'linear-gradient(135deg,#dc2626,#b91c1c)' },
    outline: {},
  };
  const sizes = { sm: 'px-3 py-1.5 text-xs', md: 'px-4 py-2.5 text-sm', lg: 'px-5 py-3 text-sm' };
  return (
    <button onClick={onClick} disabled={disabled}
      className={`rounded-xl font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed ${colors[color]} ${sizes[size]} ${className}`}
      style={bgs[color]}>
      {children}
    </button>
  );
}

// ─── TASK CARD (shared between FM + admin) ───────────────────────────────────

function TaskCard({ task, onToggle, canManage, onEdit, onDelete, readOnly }) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 bg-white hover:border-gray-300 transition-all">
      {!readOnly && (
        <button onClick={() => onToggle(task)}
          className={`mt-0.5 w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${
            task.completed_today
              ? 'bg-green-500 border-green-500 text-white'
              : 'border-gray-300 hover:border-green-400 hover:bg-green-50'
          }`}>
          {task.completed_today && <HiOutlineCheck className="w-3.5 h-3.5" />}
        </button>
      )}

      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm leading-snug text-gray-800">{task.title}</p>
        {task.description && (
          <p className="text-xs text-gray-500 mt-0.5 leading-snug">{task.description}</p>
        )}
        {!readOnly && task.completed_today && task.completion && (
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-green-600 flex items-center gap-1">
              <HiCheckCircle className="w-3.5 h-3.5" />
              Done by {task.completion.completed_by}
            </span>
            {task.completion.time_spent > 0 && (
              <span className="text-xs font-semibold text-blue-600 flex items-center gap-1">
                <HiOutlineClock className="w-3 h-3" />{fmtMinutes(task.completion.time_spent)}
              </span>
            )}
            {task.completion.notes && (
              <span className="text-xs text-gray-500 italic">"{task.completion.notes}"</span>
            )}
          </div>
        )}
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {task.assigned_user && (
            <span className="text-xs text-blue-500">Assigned to: {task.assigned_user.name}</span>
          )}
          {task.estimated_minutes > 0 && (
            <span className="text-xs text-gray-400 flex items-center gap-0.5">
              <HiOutlineClock className="w-3 h-3" />Est: {fmtMinutes(task.estimated_minutes)}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        {task.task_type === 'one_time' ? (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-600">✅ One Time</span>
        ) : (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600">🔁 Daily</span>
        )}
        {task.priority && (
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
            task.priority === 'high'   ? 'bg-red-100 text-red-600' :
            task.priority === 'medium' ? 'bg-amber-100 text-amber-600' :
                                         'bg-gray-100 text-gray-500'
          }`}>
            {task.priority === 'high' ? '🔴' : task.priority === 'medium' ? '🟡' : '🟢'} {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
          </span>
        )}
        {canManage && <>
          <button onClick={() => onEdit(task)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all">
            <HiOutlinePencil className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(task.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all">
            <HiOutlineTrash className="w-3.5 h-3.5" />
          </button>
        </>}
      </div>
    </div>
  );
}

// ─── COMPLAINT CARD (shared) ─────────────────────────────────────────────────

const ROLE_LABEL = { admin: 'Admin', superadmin: 'Super Admin', caretaker: 'Care Taker', resident: 'Resident' };
const ROLE_CLS   = { admin: 'bg-blue-100 text-blue-700', superadmin: 'bg-purple-100 text-purple-700', caretaker: 'bg-green-100 text-green-700', resident: 'bg-gray-100 text-gray-600' };

function ComplaintCard({ complaint, onUpdate, userRole, compact }) {
  const [expanded, setExpanded]   = useState(false);
  const [comment, setComment]     = useState('');
  const [saving, setSaving]       = useState(false);
  const [action, setAction]       = useState(null); // 'finish' | 'resolve'
  const [actionNote, setActionNote] = useState('');
  const [timeSpent, setTimeSpent] = useState('');

  const s = STATUS_META[complaint.status] || STATUS_META.open;
  const p = PRIORITY_META[complaint.priority || 'medium'];
  const isAdmin = userRole === 'admin' || userRole === 'superadmin';
  const isCaretaker = userRole === 'caretaker';

  const hasAdminComment = (complaint.comments || []).some(c =>
    c.commenter_role === 'admin' || c.commenter_role === 'superadmin'
  );

  const canFinish   = isCaretaker && complaint.status === 'open';
  const canResolve  = isAdmin && complaint.status === 'finished';

  const handleComment = async () => {
    if (!comment.trim()) return;
    setSaving(true);
    try {
      const updated = await addComplaintComment(complaint.id, comment.trim());
      setComment('');
      onUpdate(updated);
    } finally { setSaving(false); }
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      const mins = timeSpent ? parseInt(timeSpent, 10) : null;
      const updated = await finishFacilityComplaint(complaint.id, actionNote.trim() || null, mins || null);
      setAction(null); setActionNote(''); setTimeSpent('');
      onUpdate(updated);
    } finally { setSaving(false); }
  };

  const handleResolve = async () => {
    setSaving(true);
    try {
      const updated = await resolveFacilityComplaint(complaint.id, actionNote.trim() || null);
      setAction(null); setActionNote('');
      onUpdate(updated);
    } finally { setSaving(false); }
  };

  return (
    <div className={`rounded-xl border transition-all ${complaint.status === 'resolved' ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200 hover:border-orange-200 hover:shadow-sm'}`}>
      {/* Header row */}
      <div className="flex items-start gap-3 p-4">
        <div className={`w-1 self-stretch rounded-full flex-shrink-0 ${
          complaint.priority === 'high' ? 'bg-red-500' : complaint.priority === 'medium' ? 'bg-amber-400' : 'bg-gray-300'
        }`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>
            <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${p.cls}`}>{p.label}</span>
            {complaint.raised_by_unit && (
              <span className="text-[11px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{complaint.raised_by_unit}</span>
            )}
          </div>
          <p className="font-semibold text-sm text-gray-800 leading-snug">{complaint.title}</p>
          {complaint.description && !compact && (
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">{complaint.description}</p>
          )}
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <HiOutlineUser className="w-3.5 h-3.5" />{complaint.raised_by_name}
              {complaint.raised_by_phone && <><span className="text-gray-300">·</span>{complaint.raised_by_phone}</>}
            </span>
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <HiOutlineClock className="w-3.5 h-3.5" />{fmtDate(complaint.created_at)}
            </span>
            {(complaint.comments || []).length > 0 && (
              <span className="text-xs text-orange-500 font-medium">{complaint.comments.length} comment{complaint.comments.length > 1 ? 's' : ''}</span>
            )}
          </div>
          {complaint.status === 'finished' && complaint.finished_by_user && (
            <p className="text-xs text-blue-600 mt-1.5">Marked finished by {complaint.finished_by_user.name} · {fmtDate(complaint.finished_at)}</p>
          )}
          {complaint.status === 'resolved' && complaint.resolved_by_user && (
            <div className="mt-2 bg-green-50 border border-green-200 rounded-lg p-2.5">
              <p className="text-xs font-medium text-green-700">Resolved by {complaint.resolved_by_user.name} · {fmtDate(complaint.resolved_at)}</p>
              {complaint.resolution_notes && <p className="text-xs text-gray-600 mt-0.5">{complaint.resolution_notes}</p>}
            </div>
          )}
        </div>
        <div className="flex-shrink-0 flex flex-col items-end gap-2">
          {complaint.photo_url && (
            <a href={complaint.photo_url} target="_blank" rel="noreferrer"
              className="block rounded-lg overflow-hidden border border-gray-200 hover:border-blue-300 transition-all shadow-sm"
              title="View full photo">
              <img src={complaint.photo_url} alt="Complaint" className="w-16 h-16 object-cover" />
            </a>
          )}
          <button onClick={() => setExpanded(x => !x)}
            className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-0.5">
            {expanded ? 'Hide' : 'Details'}
            <HiOutlineChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* Expanded: comments + actions */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 pb-4 pt-3 space-y-3">
          {/* Full photo */}
          {complaint.photo_url && (
            <a href={complaint.photo_url} target="_blank" rel="noreferrer"
              className="block rounded-xl overflow-hidden border border-gray-200 hover:border-blue-300 transition-all">
              <img src={complaint.photo_url} alt="Complaint photo" className="w-full max-h-64 object-cover" />
            </a>
          )}
          {/* Comments thread */}
          {(complaint.comments || []).length > 0 && (
            <div className="space-y-2">
              {complaint.comments.map(cm => (
                <div key={cm.id} className={`rounded-lg p-2.5 text-xs ${
                  cm.commenter_role === 'admin' || cm.commenter_role === 'superadmin'
                    ? 'bg-blue-50 border border-blue-100'
                    : cm.commenter_role === 'caretaker'
                      ? 'bg-green-50 border border-green-100'
                      : 'bg-gray-50 border border-gray-100'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${ROLE_CLS[cm.commenter_role] || 'bg-gray-100 text-gray-600'}`}>
                      {ROLE_LABEL[cm.commenter_role] || cm.commenter_role}
                    </span>
                    <span className="font-medium text-gray-700">{cm.commenter_name}</span>
                    <span className="text-gray-400 ml-auto">{fmtDate(cm.created_at)} {fmtTime(cm.created_at)}</span>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{cm.comment}</p>
                </div>
              ))}
            </div>
          )}

          {/* Action buttons (role-specific, non-resolved) */}
          {complaint.status !== 'resolved' && (
            <>
              {/* Caretaker: Mark Finished */}
              {canFinish && !action && (
                <Btn size="sm" color="primary" onClick={() => setAction('finish')}>
                  Mark as Finished
                </Btn>
              )}
              {canFinish && action === 'finish' && (
                <div className="space-y-2 bg-blue-50 border border-blue-100 rounded-xl p-3">
                  <p className="text-xs font-semibold text-blue-800">Mark complaint as finished</p>
                  <textarea value={actionNote} onChange={e => setActionNote(e.target.value)}
                    rows={2} placeholder="What was done? (optional)"
                    className={inputCls + ' text-xs'} />
                  <input type="number" min="1" value={timeSpent} onChange={e => setTimeSpent(e.target.value)}
                    placeholder="Time spent (minutes, optional)" className={inputCls + ' text-xs'} />
                  <div className="flex gap-2">
                    <Btn size="sm" color="outline" onClick={() => setAction(null)} className="flex-1">Cancel</Btn>
                    <Btn size="sm" color="primary" disabled={saving} onClick={handleFinish} className="flex-1">
                      {saving ? 'Saving...' : 'Confirm Finished'}
                    </Btn>
                  </div>
                </div>
              )}

              {/* Admin: Verify & Resolve (only when finished) */}
              {canResolve && !action && (
                <Btn size="sm" color="green" onClick={() => setAction('resolve')}>
                  Verify & Resolve
                </Btn>
              )}
              {canResolve && action === 'resolve' && (
                <div className="space-y-2 bg-green-50 border border-green-100 rounded-xl p-3">
                  <p className="text-xs font-semibold text-green-800">Mark complaint as resolved</p>
                  <textarea value={actionNote} onChange={e => setActionNote(e.target.value)}
                    rows={2} placeholder="Verification notes (optional)"
                    className={inputCls + ' text-xs'} />
                  <div className="flex gap-2">
                    <Btn size="sm" color="outline" onClick={() => setAction(null)} className="flex-1">Cancel</Btn>
                    <Btn size="sm" color="green" disabled={saving} onClick={handleResolve} className="flex-1">
                      {saving ? 'Saving...' : 'Confirm Resolved'}
                    </Btn>
                  </div>
                </div>
              )}

              {/* Add comment (admin + caretaker, unless action panel is open) */}
              {!action && (isAdmin || isCaretaker) && (
                <div className="flex gap-2 items-start pt-1">
                  <textarea value={comment} onChange={e => setComment(e.target.value)}
                    rows={2} placeholder="Add a comment..."
                    className={inputCls + ' text-xs flex-1'} />
                  <Btn size="sm" color="outline" disabled={saving || !comment.trim()} onClick={handleComment}>
                    Send
                  </Btn>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Keep a thin ResolveModal stub for any leftover references (unused now)
function ResolveModal({ onClose }) {
  useEffect(() => { onClose(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// ─── TASK FORM MODAL ─────────────────────────────────────────────────────────

const PRIORITY_OPTIONS = [
  { value: 'low',    label: 'Low Priority',    color: 'text-gray-500' },
  { value: 'medium', label: 'Medium Priority',  color: 'text-amber-600' },
  { value: 'high',   label: 'High Priority',    color: 'text-red-600' },
];

function TaskFormModal({ task, onClose, onSaved }) {
  const [form, setForm] = useState({
    title:              task?.title || '',
    description:        task?.description || '',
    estimated_minutes:  task?.estimated_minutes ? String(task.estimated_minutes) : '',
    priority:           task?.priority || 'low',
    task_type:          task?.task_type || 'daily',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.title.trim()) return;
    if (!form.estimated_minutes || parseInt(form.estimated_minutes, 10) < 1) return;
    setSaving(true);
    try {
      const payload = {
        title:             form.title,
        description:       form.description,
        estimated_minutes: parseInt(form.estimated_minutes, 10),
        priority:          form.priority,
        task_type:         form.task_type,
      };
      const result = task
        ? await updateFacilityTask(task.id, payload)
        : await createFacilityTask(payload);
      onSaved({ ...result, task_type: form.task_type });
    } finally { setSaving(false); }
  };

  return (
    <Modal title={task ? 'Edit Task' : 'Add Task'} onClose={onClose}>
      <div className="space-y-4">
        {/* Task Type Selection */}
        <Field label="Task Type" required>
          <div className="grid grid-cols-2 gap-2">
            {[
              { value: 'daily',    label: 'Daily Task',    desc: 'Repeats every day',      icon: '🔁' },
              { value: 'one_time', label: 'One Time Task', desc: 'Completed once and done', icon: '✅' },
            ].map(opt => (
              <button key={opt.value} type="button"
                onClick={() => setForm(f => ({ ...f, task_type: opt.value }))}
                className={`flex flex-col items-start gap-0.5 px-3 py-2.5 rounded-xl border-2 text-left transition-all ${
                  form.task_type === opt.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}>
                <span className="text-base leading-none">{opt.icon}</span>
                <span className={`text-sm font-semibold mt-1 ${form.task_type === opt.value ? 'text-blue-700' : 'text-gray-700'}`}>
                  {opt.label}
                </span>
                <span className="text-[11px] text-gray-400">{opt.desc}</span>
              </button>
            ))}
          </div>
        </Field>

        <Field label="Task Title" required>
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="e.g. Check lobby lights" className={inputCls} />
        </Field>
        <Field label="Description">
          <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            rows={2} placeholder="Optional details..." className={inputCls} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Estimated Time (minutes)" required>
            <input type="number" min="1" value={form.estimated_minutes}
              onChange={e => setForm(f => ({ ...f, estimated_minutes: e.target.value }))}
              placeholder="e.g. 30" className={inputCls} />
            {form.estimated_minutes && parseInt(form.estimated_minutes) > 0 && (
              <p className="text-xs text-blue-500 mt-1">{fmtMinutes(parseInt(form.estimated_minutes, 10))}</p>
            )}
          </Field>
          <Field label="Priority">
            <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
              className={inputCls}>
              {PRIORITY_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>
        </div>
        <div className="flex gap-3 pt-1">
          <Btn color="outline" onClick={onClose} className="flex-1">Cancel</Btn>
          <Btn disabled={saving || !form.title.trim()} onClick={handleSubmit} className="flex-1">
            {saving ? 'Saving...' : task ? 'Update' : 'Add Task'}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

// ─── WORK FORM MODAL ─────────────────────────────────────────────────────────

function WorkFormModal({ work, onClose, onSaved }) {
  const [form, setForm] = useState({
    title:       work?.title || '',
    description: work?.description || '',
    time_spent:  work?.time_spent ? String(work.time_spent) : '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const payload = {
        title:       form.title,
        description: form.description,
        time_spent:  form.time_spent ? parseInt(form.time_spent, 10) : null,
      };
      const result = work
        ? await updateFacilityOtherWork(work.id, payload)
        : await createFacilityOtherWork(payload);
      onSaved(result);
    } finally { setSaving(false); }
  };

  return (
    <Modal title={work ? 'Edit Work' : 'Log Other Work'} onClose={onClose}>
      <div className="space-y-4">
        <Field label="Work Title" required>
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="e.g. Fixed gate hinge" className={inputCls} />
        </Field>
        <Field label="Description">
          <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            rows={3} placeholder="Details about this work..." className={inputCls} />
        </Field>
        <Field label="Time Spent (minutes)" required>
          <input type="number" min="1" value={form.time_spent}
            onChange={e => setForm(f => ({ ...f, time_spent: e.target.value }))}
            placeholder="e.g. 90  →  1h 30m" className={inputCls} />
          {form.time_spent && parseInt(form.time_spent) > 0 && (
            <p className="text-xs text-blue-500 mt-1">{fmtMinutes(parseInt(form.time_spent, 10))}</p>
          )}
        </Field>
        <div className="flex gap-3 pt-1">
          <Btn color="outline" onClick={onClose} className="flex-1">Cancel</Btn>
          <Btn disabled={saving || !form.title.trim()} onClick={handleSubmit} className="flex-1">
            {saving ? 'Saving...' : work ? 'Update' : 'Log Work'}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

// ─── MARK DONE MODAL ─────────────────────────────────────────────────────────

function MarkDoneModal({ work, onClose, onDone }) {
  const [notes, setNotes]           = useState('');
  const [timeSpent, setTimeSpent]   = useState('');
  const [saving, setSaving]         = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const mins = timeSpent ? parseInt(timeSpent, 10) : null;
      const updated = await markFacilityOtherWorkDone(work.id, notes, mins || null);
      onDone(updated);
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Mark Work as Done" onClose={onClose}>
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
          <p className="text-sm font-semibold text-gray-800">{work.title}</p>
          {work.est_time && <p className="text-xs text-gray-500 mt-0.5">Est. time: {work.est_time}</p>}
        </div>
        <Field label="Actual Time Spent (minutes)">
          <input type="number" min="1" value={timeSpent} onChange={e => setTimeSpent(e.target.value)}
            placeholder="e.g. 45  →  45 min" className={inputCls} />
        </Field>
        <Field label="Completion Notes (optional)">
          <textarea value={notes} onChange={e => setNotes(e.target.value)}
            rows={3} placeholder="Any notes about how it was completed..."
            className={inputCls} />
        </Field>
        <div className="flex gap-3 pt-1">
          <Btn color="outline" onClick={onClose} className="flex-1">Cancel</Btn>
          <Btn color="green" disabled={saving} onClick={handleSubmit} className="flex-1">
            {saving ? 'Saving...' : 'Mark Done'}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FM DASHBOARD VIEW  (caretaker role)
// ═══════════════════════════════════════════════════════════════════════════

function TaskCompleteModal({ task, onClose, onDone, editMode, date }) {
  const existing = task.completion;
  const [notes, setNotes] = useState(editMode ? (existing?.notes || '') : '');
  const [saving, setSaving] = useState(false);

  const autoMins = task.estimated_minutes || null;

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const updated = editMode
        ? await updateFacilityTaskCompletion(task.id, notes, existing?.time_spent || autoMins, date)
        : await completeFacilityTask(task.id, notes, autoMins, date);
      onDone(updated);
    } finally { setSaving(false); }
  };

  return (
    <Modal title={editMode ? 'Edit Task Completion' : 'Mark Task Complete'} onClose={onClose}>
      <div className="space-y-4">
        <div className="bg-green-50 border border-green-200 rounded-xl p-3">
          <p className="text-sm font-semibold text-gray-800">{task.title}</p>
          {task.description && <p className="text-xs text-gray-500 mt-0.5">{task.description}</p>}
          {autoMins && (
            <p className="text-xs text-blue-600 mt-1.5 flex items-center gap-1">
              <HiOutlineClock className="w-3.5 h-3.5" />
              Estimated time: <span className="font-semibold">{fmtMinutes(autoMins)}</span>
            </p>
          )}
        </div>
        <Field label="Notes (optional)">
          <input value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Any remarks about this task..." className={inputCls} />
        </Field>
        <div className="flex gap-3 pt-1">
          <Btn color="outline" onClick={onClose} className="flex-1">Cancel</Btn>
          <Btn color="green" disabled={saving} onClick={handleSubmit} className="flex-1">
            {saving ? 'Saving...' : editMode ? 'Update' : 'Mark Complete'}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

const FM_TABS = [
  { key: 'tasks',      label: 'Tasks',                icon: HiOutlineClipboardCheck },
  { key: 'complaints', label: 'Complaints to Resolve', icon: HiOutlineExclamationCircle },
  { key: 'works',      label: 'My Other Works',        icon: HiOutlineBriefcase },
];

const LEAVE_REASONS = [
  { value: 'leave',   label: 'Leave',   icon: '🏖️' },
  { value: 'holiday', label: 'Holiday', icon: '🎉' },
  { value: 'off',     label: 'Day Off',  icon: '😴' },
];

function LeaveReasonModal({ initialDate, onClose, onMarked }) {
  const todayISO = new Date().toISOString().slice(0, 10);
  const [date, setDate]     = useState(initialDate || todayISO);
  const [reason, setReason] = useState('leave');
  const [notes, setNotes]   = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!date) return;
    setSaving(true);
    try {
      await markFMLeaveDay(date, reason, notes || null);
      onMarked({ date, reason });
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Mark Day as Leave / Off" onClose={onClose}>
      <div className="space-y-4">
        <Field label="Date" required>
          <input
            type="date"
            value={date}
            max={todayISO}
            onChange={e => setDate(e.target.value)}
            className={inputCls}
          />
          {date === todayISO && (
            <p className="text-xs text-blue-500 mt-1">Marking today — tasks will be paused for the day.</p>
          )}
          {date && date !== todayISO && (
            <p className="text-xs text-amber-600 mt-1">
              {date < todayISO ? 'Marking a past date as leave.' : 'Marking a future date as leave.'}
            </p>
          )}
        </Field>
        <Field label="Reason" required>
          <div className="grid grid-cols-3 gap-2">
            {LEAVE_REASONS.map(r => (
              <button key={r.value} type="button"
                onClick={() => setReason(r.value)}
                className={`flex flex-col items-center gap-1 px-3 py-3 rounded-xl border-2 text-center transition-all ${
                  reason === r.value ? 'border-orange-500 bg-orange-50' : 'border-gray-200 bg-white hover:border-gray-300'
                }`}>
                <span className="text-xl">{r.icon}</span>
                <span className={`text-xs font-semibold ${reason === r.value ? 'text-orange-700' : 'text-gray-600'}`}>{r.label}</span>
              </button>
            ))}
          </div>
        </Field>
        <Field label="Notes (optional)">
          <input value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="e.g. Annual leave, Public holiday..." className={inputCls} />
        </Field>
        <div className="flex gap-3 pt-1">
          <Btn color="outline" onClick={onClose} className="flex-1">Cancel</Btn>
          <Btn color="danger" disabled={saving || !date} onClick={handleSubmit} className="flex-1">
            {saving ? 'Saving...' : 'Mark as Off / Leave'}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

function PendingFromLastWorkingDay({ data, onCompleteTask }) {
  const [expanded, setExpanded] = useState(false);
  if (!data?.date) return null;
  const total = (data.pending?.tasks?.length || 0) + (data.pending?.complaints?.length || 0);
  if (total === 0) return null;

  const dateLabel = (() => {
    const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
    if (data.date === yesterday.toISOString().slice(0, 10)) return 'Yesterday';
    return fmtDate(data.date);
  })();

  return (
    <div className="rounded-xl border border-amber-200 overflow-hidden">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 bg-amber-50 border-b border-amber-200 hover:bg-amber-100 transition-colors">
        <HiOutlineBell className="w-4 h-4 text-amber-500 flex-shrink-0" />
        <span className="text-sm font-semibold text-amber-700 flex-1 text-left">
          {dateLabel}'s Pending — {total} item{total !== 1 ? 's' : ''} not completed
        </span>
        <span className="text-xs text-amber-500 font-medium">{expanded ? 'Hide' : 'Show'}</span>
        <HiOutlineChevronDown className={`w-4 h-4 text-amber-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="bg-white divide-y divide-gray-100">
          {data.pending?.tasks?.map(t => (
            <div key={t.id} className="flex items-center gap-3 px-4 py-2.5">
              <HiOutlineClipboardCheck className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="flex-1 text-sm text-gray-700">{t.title}</span>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full mr-1 ${
                t.priority === 'high' ? 'bg-red-100 text-red-600' :
                t.priority === 'medium' ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-500'
              }`}>
                {t.priority === 'high' ? '🔴' : t.priority === 'medium' ? '🟡' : '🟢'} {t.priority}
              </span>
              <button
                onClick={() => onCompleteTask(t, data.date)}
                className="text-xs font-medium px-3 py-1 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors flex-shrink-0">
                Complete
              </button>
            </div>
          ))}
          {data.pending?.complaints?.map(c => (
            <div key={c.id} className="flex items-center gap-3 px-4 py-2.5">
              <HiOutlineExclamationCircle className="w-4 h-4 text-orange-400 flex-shrink-0" />
              <span className="flex-1 text-sm text-gray-700">{c.title}</span>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full mr-1 ${
                c.priority === 'high' ? 'bg-red-100 text-red-600' :
                c.priority === 'medium' ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-500'
              }`}>
                {c.priority === 'high' ? '🔴' : c.priority === 'medium' ? '🟡' : '🟢'} {c.priority}
              </span>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 flex-shrink-0">
                Pending
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FMDashboard({ user }) {
  const todayISO = new Date().toISOString().slice(0, 10);

  const [selectedDate, setSelectedDate] = useState(todayISO);
  const isToday = selectedDate === todayISO;

  const [tasks, setTasks]           = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [works, setWorks]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [activeTab, setActiveTab]   = useState('tasks');
  const [completeTarget, setCompleteTarget] = useState(null);
  const [editComplete, setEditComplete]     = useState(null);
  const [workModal, setWorkModal]   = useState(null);
  const [editWork, setEditWork]     = useState(null);
  const [worksDateFilter, setWorksDateFilter] = useState(todayISO);
  const [taskPriorityFilter, setTaskPriorityFilter] = useState('');

  // Leave / off day state
  const [todayLeave, setTodayLeave]           = useState(null); // null=loading, false=working, {reason,...}=on leave
  const [selectedDateLeave, setSelectedDateLeave] = useState(null); // leave status for selectedDate
  const [leaveModal, setLeaveModal]           = useState(false);
  const [markingLeave, setMarkingLeave]       = useState(false);

  // Last working day pending (replaces fixed "yesterday" pending)
  const [lastWorkingDay, setLastWorkingDay] = useState(null); // { date, pending }
  const [missedCompleteTarget, setMissedCompleteTarget] = useState(null); // { task, date }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, c, w] = await Promise.all([
        getFacilityTasks(selectedDate),
        getFacilityComplaints(),
        getFacilityOtherWorks(),
      ]);
      setTasks(t);
      setComplaints(c.filter(x => x.status !== 'resolved' && x.status !== 'in_progress'));
      setWorks(w);
    } finally { setLoading(false); }
  }, [selectedDate]);

  // Check today's leave status and load last working day pending
  useEffect(() => {
    checkFMLeaveDay(todayISO)
      .then(data => {
        const leave = data.is_leave ? data.leave : false;
        setTodayLeave(leave);
        if (selectedDate === todayISO) setSelectedDateLeave(leave);
      })
      .catch(() => { setTodayLeave(false); if (selectedDate === todayISO) setSelectedDateLeave(false); });

    getFMLastWorkingDayPending()
      .then(data => setLastWorkingDay(data))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-check leave status whenever selected date changes
  useEffect(() => {
    setSelectedDateLeave(null);
    checkFMLeaveDay(selectedDate)
      .then(data => setSelectedDateLeave(data.is_leave ? data.leave : false))
      .catch(() => setSelectedDateLeave(false));
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleMarkLeave = async ({ date, reason }) => {
    setLeaveModal(false);
    const leaveRecord = { reason, date };
    if (date === todayISO) setTodayLeave(leaveRecord);
    if (date === selectedDate) setSelectedDateLeave(leaveRecord);
    // Refresh last working day pending in case a past leave day was just marked
    getFMLastWorkingDayPending().then(setLastWorkingDay).catch(() => {});
  };

  const handleUnmarkLeave = async () => {
    setMarkingLeave(true);
    try {
      await unmarkFMLeaveDay(selectedDate);
      if (selectedDate === todayISO) setTodayLeave(false);
      setSelectedDateLeave(false);
      getFMLastWorkingDayPending().then(setLastWorkingDay).catch(() => {});
    } finally { setMarkingLeave(false); }
  };

  const toggleTask = async (task) => {
    if (task.completed_today) {
      const updated = await uncompleteFacilityTask(task.id, selectedDate);
      setTasks(ts => ts.map(t => t.id === updated.id ? updated : t));
    } else {
      setCompleteTarget(task);
    }
  };

  const todayStr = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' });
  const doneTasks = tasks.filter(t => t.completed_today).length;
  const filteredTasks = taskPriorityFilter ? tasks.filter(t => t.priority === taskPriorityFilter) : tasks;

  const effectiveMins = (w) => {
    const ts = parseInt(w.time_spent, 10);
    if (ts > 0) return ts;
    const et = parseFloat(w.est_time);
    return !isNaN(et) ? Math.round(et * 60) : 0;
  };

  const todayWorks = works.filter(w => w.created_at?.slice(0, 10) === todayISO);

  const totalMinutes =
    todayWorks.reduce((sum, w) => sum + effectiveMins(w), 0) +
    tasks
      .filter(t => t.completed_today && t.completion?.time_spent)
      .reduce((sum, t) => sum + (parseInt(t.completion.time_spent, 10) || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading your tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Greeting / Stats Banner ── */}
      <div className="rounded-2xl p-5 text-white relative overflow-hidden"
        style={{ background: selectedDateLeave
          ? 'linear-gradient(135deg,#78350f 0%,#92400e 100%)'
          : 'linear-gradient(135deg,#032841 0%,#0a5276 100%)' }}>
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full opacity-10 bg-white" />
        <div className="absolute -right-4 bottom-0 w-24 h-24 rounded-full opacity-5 bg-white" />
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs opacity-60 mb-0.5">{todayStr}</p>
            <h2 className="text-xl font-bold mb-1">
              {selectedDateLeave ? (
                <span>
                  {selectedDateLeave.reason === 'leave' ? '🏖️' : selectedDateLeave.reason === 'holiday' ? '🎉' : '😴'}{' '}
                  {user?.name?.split(' ')[0]} — {LEAVE_REASONS.find(r => r.value === selectedDateLeave.reason)?.label || 'Off'}
                  {!isToday && <span className="text-base font-normal opacity-70"> · {fmtDate(selectedDate)}</span>}
                </span>
              ) : `Good day, ${user?.name?.split(' ')[0]}!`}
            </h2>
            {selectedDateLeave && (
              <p className="text-xs opacity-70 mb-3">
                {isToday ? 'Today' : fmtDate(selectedDate)} is marked as {selectedDateLeave.reason}{selectedDateLeave.notes ? ` · ${selectedDateLeave.notes}` : ''}.
                {isToday && ' Use the date picker to view a past day.'}
              </p>
            )}
          </div>
          {/* Leave toggle — shown for any selected date */}
          <div className="flex-shrink-0">
            {selectedDateLeave !== null && (selectedDateLeave ? (
              <button onClick={handleUnmarkLeave} disabled={markingLeave}
                className="text-xs font-semibold px-3 py-1.5 rounded-xl bg-white/20 hover:bg-white/30 border border-white/30 transition-all disabled:opacity-50">
                {markingLeave ? 'Removing...' : '✓ Mark as Working'}
              </button>
            ) : (
              <button onClick={() => setLeaveModal(true)}
                className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-xl bg-white text-gray-800 hover:bg-gray-100 shadow-md transition-all">
                <span>🏖️</span> {isToday ? 'Mark as Leave / Off' : 'Mark Day as Leave / Off'}
              </button>
            ))}
          </div>
        </div>

        {!selectedDateLeave && (
          <>
            <div className="grid grid-cols-4 gap-3">
              {[
                { value: `${doneTasks}/${tasks.length}`, label: 'Tasks Done',        active: activeTab === 'tasks',      tab: 'tasks' },
                { value: complaints.length,              label: 'Complaints Pending', active: activeTab === 'complaints', tab: 'complaints' },
                { value: todayWorks.length,              label: 'Works Logged',       active: activeTab === 'works',      tab: 'works' },
                { value: fmtMinutes(totalMinutes) || '0m', label: 'Time Logged',     active: false,                      tab: null },
              ].map(s => (
                <button key={s.label}
                  onClick={() => s.tab && setActiveTab(s.tab)}
                  className={`text-center rounded-xl p-2.5 transition-all ${s.tab ? 'cursor-pointer hover:bg-white/20' : 'cursor-default'} ${s.active ? 'bg-white/20 ring-1 ring-white/40' : ''}`}>
                  <p className="text-2xl font-bold leading-none">{s.value}</p>
                  <p className="text-[11px] opacity-60 mt-1 leading-tight">{s.label}</p>
                </button>
              ))}
            </div>
            {tasks.length > 0 && (
              <div className="mt-4">
                <div className="h-1.5 bg-white/20 rounded-full overflow-hidden w-full">
                  <div className="h-full bg-white rounded-full transition-all"
                    style={{ width: `${(doneTasks / tasks.length) * 100}%` }} />
                </div>
                <p className="text-[11px] opacity-50 mt-1">{Math.round((doneTasks / tasks.length) * 100)}% of tasks complete</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Date Navigation Bar ── */}
      <div className="flex items-center gap-2 bg-white rounded-2xl border border-gray-200 px-4 py-2.5 shadow-sm">
        <button
          onClick={() => {
            const d = new Date(selectedDate); d.setDate(d.getDate() - 1);
            setSelectedDate(d.toISOString().slice(0, 10));
          }}
          className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all">
          ‹ Prev
        </button>
        <div className="flex-1 flex items-center justify-center gap-2">
          <HiOutlineCalendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input
            type="date"
            value={selectedDate}
            max={todayISO}
            onChange={e => { if (e.target.value) setSelectedDate(e.target.value); }}
            className="text-sm font-semibold text-gray-700 bg-transparent border-0 focus:outline-none cursor-pointer"
          />
        </div>
        {!isToday ? (
          <button
            onClick={() => setSelectedDate(todayISO)}
            className="text-xs font-semibold px-3 py-1.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-all">
            Go to Today
          </button>
        ) : (
          <button
            disabled
            className="p-1.5 text-gray-200 rounded-lg cursor-not-allowed">
            Next ›
          </button>
        )}
        {isToday && (
          <button
            onClick={() => {
              const d = new Date(selectedDate); d.setDate(d.getDate() + 1);
              const next = d.toISOString().slice(0, 10);
              if (next <= todayISO) setSelectedDate(next);
            }}
            disabled={isToday}
            className="hidden">
          </button>
        )}
      </div>

      {/* ── Last Working Day Pending (collapsed, leave-aware) ── */}
      {!selectedDateLeave && isToday && (
        <PendingFromLastWorkingDay
          data={lastWorkingDay}
          onCompleteTask={(task, date) => setMissedCompleteTarget({ task, date })}
        />
      )}

      {/* ── Tab Bar (hidden when selected date is marked as leave) ── */}
      {!selectedDateLeave && (
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          {FM_TABS.map(({ key, label, icon: Icon }) => {
            const badge = key === 'tasks' ? `${doneTasks}/${tasks.length}`
              : key === 'complaints' ? (complaints.length > 0 ? complaints.length : null)
              : todayWorks.length > 0 ? todayWorks.length : null;
            return (
              <button key={key} onClick={() => setActiveTab(key)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === key ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}>
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="hidden sm:inline">{label}</span>
                {badge != null && (
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                    activeTab === key ? 'bg-gray-100 text-gray-600' : 'bg-gray-200 text-gray-500'
                  }`}>{badge}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Leave day message (shown when selected date is marked as leave) ── */}
      {selectedDateLeave && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-10 text-center">
          <p className="text-4xl mb-3">
            {selectedDateLeave.reason === 'leave' ? '🏖️' : selectedDateLeave.reason === 'holiday' ? '🎉' : '😴'}
          </p>
          <p className="font-semibold text-amber-800 text-lg">
            {LEAVE_REASONS.find(r => r.value === selectedDateLeave.reason)?.label || 'Day Off'}
            {!isToday && <span className="font-normal text-amber-700 text-base"> · {fmtDate(selectedDate)}</span>}
          </p>
          <p className="text-sm text-amber-600 mt-1">
            {isToday
              ? `Today's tasks are paused. Enjoy your ${selectedDateLeave.reason === 'holiday' ? 'holiday' : 'day off'}!`
              : `This day was marked as ${selectedDateLeave.reason}.`}
          </p>
          {selectedDateLeave.notes && (
            <p className="text-xs text-amber-500 mt-2 italic">"{selectedDateLeave.notes}"</p>
          )}
        </div>
      )}

      {/* ── Tab Content ── */}
      {!selectedDateLeave && <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

        {/* Today's Tasks */}
        {activeTab === 'tasks' && (
          <>
            <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between"
              style={{ background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)' }}>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-green-600 rounded-lg flex items-center justify-center">
                  <HiOutlineClipboardCheck className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm">
                    Tasks {!isToday && <span className="text-green-600 font-normal">· {fmtDate(selectedDate)}</span>}
                  </h3>
                  <p className="text-xs text-green-600">{doneTasks} of {tasks.length} completed</p>
                </div>
              </div>
              <button onClick={load} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-white/60 rounded-lg">
                <HiOutlineRefresh className="w-4 h-4" />
              </button>
            </div>
            {/* Priority filter chips */}
            <div className="px-4 pt-3 pb-1 flex items-center gap-1.5 flex-wrap">
              {PRIORITY_FILTERS.map(f => (
                <button
                  key={f.key}
                  onClick={() => setTaskPriorityFilter(f.key)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${
                    taskPriorityFilter === f.key
                      ? 'bg-gray-800 text-white border-gray-800'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="p-4 space-y-2">
              {filteredTasks.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <HiOutlineClipboardCheck className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">
                    {tasks.length === 0
                      ? (isToday ? 'No tasks assigned for today' : `No tasks for ${fmtDate(selectedDate)}`)
                      : 'No tasks match the selected priority'}
                  </p>
                </div>
              ) : (
                filteredTasks.map(task => (
                  <div key={task.id} className="relative group">
                    <TaskCard task={task} onToggle={toggleTask} canManage={false} />
                    {task.completed_today && (
                      <button
                        onClick={() => setEditComplete(task)}
                        className="absolute top-2.5 right-2.5 p-1.5 text-gray-300 hover:text-blue-500 hover:bg-blue-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all">
                        <HiOutlinePencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* Complaints */}
        {activeTab === 'complaints' && (
          <>
            <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between"
              style={{ background: 'linear-gradient(135deg,#fff7ed,#ffedd5)' }}>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-orange-500 rounded-lg flex items-center justify-center">
                  <HiOutlineExclamationCircle className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm">Complaints to Resolve</h3>
                  <p className="text-xs text-orange-600">{complaints.length} pending</p>
                </div>
              </div>
              <button onClick={load} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-white/60 rounded-lg">
                <HiOutlineRefresh className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {complaints.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <HiOutlineExclamationCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No pending complaints — great job!</p>
                </div>
              ) : (
                complaints.map(c => (
                  <ComplaintCard key={c.id} complaint={c} compact userRole={user?.role}
                    onUpdate={updated => setComplaints(cs => cs.map(x => x.id === updated.id ? updated : x))} />
                ))
              )}
            </div>
          </>
        )}

        {/* Other Works */}
        {activeTab === 'works' && (
          <>
            <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between"
              style={{ background: 'linear-gradient(135deg,#f5f3ff,#ede9fe)' }}>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-violet-600 rounded-lg flex items-center justify-center">
                  <HiOutlineBriefcase className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm">My Other Works</h3>
                  <p className="text-xs text-violet-600">
                    {worksDateFilter === todayISO
                      ? `${todayWorks.length} logged today`
                      : worksDateFilter
                        ? `${works.filter(w => w.created_at?.slice(0,10) === worksDateFilter).length} on ${fmtDate(worksDateFilter)}`
                        : `${works.length} total`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={worksDateFilter}
                  onChange={e => setWorksDateFilter(e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-violet-400 text-gray-600"
                />
                {worksDateFilter && (
                  <button onClick={() => setWorksDateFilter('')}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-white/60 rounded-lg">
                    <HiOutlineX className="w-3.5 h-3.5" />
                  </button>
                )}
                <Btn size="sm" onClick={() => setWorkModal('add')}>
                  <span className="flex items-center gap-1"><HiOutlinePlus className="w-3.5 h-3.5" /> Log Work</span>
                </Btn>
              </div>
            </div>
            <div className="p-4 space-y-3">
              {(() => {
                const filtered = worksDateFilter
                  ? works.filter(w => w.created_at?.slice(0, 10) === worksDateFilter)
                  : works;
                if (filtered.length === 0) return (
                  <div className="text-center py-12 text-gray-400">
                    <HiOutlineBriefcase className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">{worksDateFilter === todayISO ? 'No works logged today' : worksDateFilter ? 'No works logged on this date' : 'No works logged yet'}</p>
                  </div>
                );
                return filtered.map(w => (
                  <div key={w.id} className="flex items-start gap-3 p-4 rounded-xl border bg-white border-gray-200 hover:border-gray-300 transition-all">
                    <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0 bg-violet-400" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-gray-800">{w.title}</p>
                      {w.description && <p className="text-xs text-gray-500 mt-0.5">{w.description}</p>}
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        {effectiveMins(w) > 0 ? (
                          <span className="text-xs font-semibold text-blue-600 flex items-center gap-1">
                            <HiOutlineClock className="w-3 h-3" />{fmtMinutes(effectiveMins(w))}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 italic">no time logged</span>
                        )}
                        <span className="text-xs text-gray-400">{fmtDate(w.created_at)}</span>
                      </div>
                    </div>
                    <button onClick={() => setEditWork(w)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg flex-shrink-0">
                      <HiOutlinePencil className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ));
              })()}
            </div>
          </>
        )}
      </div>}

      {/* ── Modals ── */}
      {leaveModal && (
        <LeaveReasonModal initialDate={selectedDate} onClose={() => setLeaveModal(false)} onMarked={handleMarkLeave} />
      )}
      {completeTarget && (
        <TaskCompleteModal task={completeTarget} date={selectedDate} onClose={() => setCompleteTarget(null)}
          onDone={updated => {
            setTasks(ts => ts.map(t => t.id === updated.id ? updated : t));
            setCompleteTarget(null);
          }} />
      )}
      {editComplete && (
        <TaskCompleteModal task={editComplete} date={selectedDate} editMode onClose={() => setEditComplete(null)}
          onDone={updated => {
            setTasks(ts => ts.map(t => t.id === updated.id ? updated : t));
            setEditComplete(null);
          }} />
      )}
      {missedCompleteTarget && (
        <TaskCompleteModal
          task={missedCompleteTarget.task}
          date={missedCompleteTarget.date}
          onClose={() => setMissedCompleteTarget(null)}
          onDone={() => {
            setLastWorkingDay(prev => prev ? {
              ...prev,
              pending: {
                ...prev.pending,
                tasks: prev.pending.tasks.filter(t => t.id !== missedCompleteTarget.task.id),
              },
            } : prev);
            setMissedCompleteTarget(null);
          }} />
      )}
      {workModal === 'add' && (
        <WorkFormModal onClose={() => setWorkModal(null)}
          onSaved={w => { setWorks(ws => [w, ...ws]); setWorkModal(null); }} />
      )}
      {editWork && (
        <WorkFormModal work={editWork} onClose={() => setEditWork(null)}
          onSaved={w => { setWorks(ws => ws.map(x => x.id === w.id ? w : x)); setEditWork(null); }} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ADMIN MANAGEMENT VIEW
// ═══════════════════════════════════════════════════════════════════════════

const ADMIN_TABS = [
  { key: 'tasks',           label: 'Tasks',           icon: HiOutlineClipboardCheck },
  { key: 'complaints',      label: 'Complaints',      icon: HiOutlineExclamationCircle },
  { key: 'other-works',     label: 'Other Works',     icon: HiOutlineBriefcase },
  { key: 'work-log',        label: 'Work Log',        icon: HiOutlineChartBar },
  { key: 'monthly-summary', label: 'Monthly Summary', icon: HiOutlineCalendar },
];

// ─── MONTHLY SUMMARY TAB ──────────────────────────────────────────────────────

const DAY_META = {
  working:     { label: 'Working',    bg: 'bg-green-500',  text: 'text-white',      light: 'bg-green-50 border-green-200',   dot: 'bg-green-500'  },
  no_activity: { label: 'No Activity',bg: 'bg-red-200',    text: 'text-red-700',    light: 'bg-red-50 border-red-200',       dot: 'bg-red-300'    },
  leave:       { label: 'Leave',      bg: 'bg-amber-400',  text: 'text-white',      light: 'bg-amber-50 border-amber-200',   dot: 'bg-amber-400'  },
  holiday:     { label: 'Holiday',    bg: 'bg-purple-500', text: 'text-white',      light: 'bg-purple-50 border-purple-200', dot: 'bg-purple-400' },
  off:         { label: 'Day Off',    bg: 'bg-gray-400',   text: 'text-white',      light: 'bg-gray-50 border-gray-200',     dot: 'bg-gray-400'   },
  future:      { label: 'Future',     bg: 'bg-gray-100',   text: 'text-gray-300',   light: 'bg-gray-50 border-gray-100',     dot: 'bg-gray-200'   },
};

const DAY_ICONS = { working: '✅', no_activity: '❌', leave: '🏖️', holiday: '🎉', off: '😴', future: '' };
const WEEKDAYS  = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function MonthlySummaryTab() {
  const todayISO = new Date().toISOString().slice(0, 10);
  const currentMonth = todayISO.slice(0, 7);

  const [month, setMonth]     = useState(currentMonth);
  const [users, setUsers]     = useState([]);
  const [userId, setUserId]   = useState('');
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [tooltip, setTooltip] = useState(null); // { day, x, y }

  useEffect(() => {
    getFMUsers().then(setUsers).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!userId) { setData(null); return; }
    setLoading(true);
    try {
      setData(await getFMonthlySummary(month, userId));
    } finally { setLoading(false); }
  }, [month, userId]);

  useEffect(() => { load(); }, [load]);

  const prevMonth = () => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 2, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  const nextMonth = () => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m, 1);
    const next = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (next <= currentMonth) setMonth(next);
  };

  const monthLabel = (() => {
    const [y, m] = month.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  })();

  // Build calendar grid (pad with nulls for first week)
  const calendarDays = (() => {
    if (!data?.days?.length) return [];
    const firstDay = new Date(data.days[0].date).getDay(); // 0=Sun
    return [...Array(firstDay).fill(null), ...data.days];
  })();

  const counts = data?.counts || {};
  const LEGEND = [
    { type: 'working',     count: counts.working     || 0 },
    { type: 'leave',       count: counts.leave        || 0 },
    { type: 'holiday',     count: counts.holiday      || 0 },
    { type: 'off',         count: counts.off          || 0 },
    { type: 'no_activity', count: counts.no_activity  || 0 },
  ];

  return (
    <div>
      {/* Controls */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        {/* User selector */}
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
          <HiOutlineUser className="w-4 h-4 text-gray-400" />
          <select value={userId} onChange={e => setUserId(e.target.value)}
            className="text-sm bg-transparent focus:outline-none text-gray-700 pr-4">
            <option value="">— Select Facility Manager —</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        {/* Month navigator */}
        <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-xl px-2 py-1.5">
          <button onClick={prevMonth} className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-all">‹</button>
          <span className="text-sm font-semibold text-gray-700 px-3 min-w-[130px] text-center">{monthLabel}</span>
          <button onClick={nextMonth} disabled={month >= currentMonth}
            className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-all disabled:opacity-30">›</button>
        </div>
        {month !== currentMonth && (
          <button onClick={() => setMonth(currentMonth)}
            className="text-xs font-semibold px-3 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-all">
            This Month
          </button>
        )}
      </div>

      {/* Prompt to select user */}
      {!userId && (
        <div className="text-center py-20 text-gray-400">
          <HiOutlineUser className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">Select a Facility Manager to view their monthly summary</p>
        </div>
      )}

      {userId && loading && (
        <div className="text-center py-16 text-gray-400 text-sm">Loading calendar...</div>
      )}

      {userId && !loading && data && (
        <>
          {/* Summary chips */}
          <div className="flex flex-wrap gap-2 mb-5">
            {LEGEND.map(({ type, count }) => {
              const m = DAY_META[type];
              return (
                <div key={type} className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border text-sm font-semibold ${m.light}`}>
                  <span className="text-base leading-none">{DAY_ICONS[type]}</span>
                  <span className={`${type === 'no_activity' ? 'text-red-600' : type === 'working' ? 'text-green-700' : type === 'leave' ? 'text-amber-700' : type === 'holiday' ? 'text-purple-700' : 'text-gray-600'}`}>
                    {m.label}
                  </span>
                  <span className={`text-lg font-bold ${type === 'no_activity' ? 'text-red-500' : type === 'working' ? 'text-green-600' : type === 'leave' ? 'text-amber-600' : type === 'holiday' ? 'text-purple-600' : 'text-gray-500'}`}>
                    {count}
                  </span>
                </div>
              );
            })}
            {counts.future > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-100 bg-gray-50 text-sm text-gray-400">
                <span className="font-semibold">{counts.future}</span> days remaining
              </div>
            )}
          </div>

          {/* Calendar grid */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            {/* Weekday headers */}
            <div className="grid grid-cols-7 border-b border-gray-100">
              {WEEKDAYS.map(d => (
                <div key={d} className={`py-2 text-center text-xs font-semibold ${d === 'Sun' ? 'text-red-400' : 'text-gray-400'}`}>
                  {d}
                </div>
              ))}
            </div>
            {/* Day cells */}
            <div className="grid grid-cols-7">
              {calendarDays.map((day, i) => {
                if (!day) return <div key={`empty-${i}`} className="aspect-square border-b border-r border-gray-50" />;
                const m    = DAY_META[day.type] || DAY_META.future;
                const dom  = new Date(day.date).getDate();
                const isToday = day.date === todayISO;
                const isSun   = new Date(day.date).getDay() === 0;
                return (
                  <div key={day.date}
                    title={day.type !== 'future' ? `${day.date}` : ''}
                    className={`relative border-b border-r border-gray-100 p-1.5 flex flex-col items-center gap-0.5 min-h-[60px]
                      ${day.type !== 'future' ? 'cursor-default hover:bg-gray-50' : ''}
                    `}>
                    {/* Date number */}
                    <span className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full
                      ${isToday ? 'bg-blue-600 text-white' : isSun ? 'text-red-400' : 'text-gray-600'}
                    `}>{dom}</span>
                    {/* Status indicator */}
                    {day.type !== 'future' && (
                      <span className={`text-[11px] leading-none font-bold px-1.5 py-0.5 rounded-full w-full text-center ${
                        day.type === 'working'     ? 'bg-green-100 text-green-700' :
                        day.type === 'no_activity' ? 'bg-red-100 text-red-600'    :
                        day.type === 'leave'       ? 'bg-amber-100 text-amber-700':
                        day.type === 'holiday'     ? 'bg-purple-100 text-purple-700':
                                                     'bg-gray-100 text-gray-500'
                      }`}>
                        {DAY_ICONS[day.type]}
                      </span>
                    )}
                    {/* Working day quick stats */}
                    {day.type === 'working' && (
                      <div className="text-[9px] text-gray-400 text-center leading-tight">
                        {day.tasks > 0 && <span>{day.tasks}T </span>}
                        {day.works > 0 && <span>{day.works}W </span>}
                        {day.minutes > 0 && <span>{fmtMinutes(day.minutes)}</span>}
                      </div>
                    )}
                    {/* Leave/off note */}
                    {(day.type === 'leave' || day.type === 'holiday' || day.type === 'off') && day.notes && (
                      <p className="text-[9px] text-gray-400 text-center leading-tight truncate w-full px-0.5">{day.notes}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-3 mt-4 text-xs text-gray-500">
            <span className="font-semibold text-gray-400">Legend:</span>
            {Object.entries(DAY_META).filter(([k]) => k !== 'future').map(([type, m]) => (
              <span key={type} className="flex items-center gap-1">
                <span className="text-sm">{DAY_ICONS[type]}</span> {m.label}
              </span>
            ))}
            <span className="flex items-center gap-1"><span className="w-5 h-5 rounded-full bg-blue-600 inline-block" /> Today</span>
          </div>
        </>
      )}
    </div>
  );
}

// ─── WORK LOG TAB ────────────────────────────────────────────────────────────

const TYPE_META = {
  task:      { label: 'Task',      dot: 'bg-green-500',  badge: 'bg-green-100 text-green-700',  border: 'border-l-green-400' },
  complaint: { label: 'Complaint', dot: 'bg-orange-500', badge: 'bg-orange-100 text-orange-700', border: 'border-l-orange-400' },
  work:      { label: 'Other Work',dot: 'bg-violet-500', badge: 'bg-violet-100 text-violet-700', border: 'border-l-violet-400' },
};

function WorkLogTab() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate]       = useState(today);
  const [users, setUsers]     = useState([]);
  const [userId, setUserId]   = useState('');
  const [log, setLog]         = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getFMUsers().then(setUsers).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getFacilityWorkLog(date, userId || undefined);
      setLog(data);
    } finally { setLoading(false); }
  }, [date, userId]);

  useEffect(() => { load(); }, [load]);

  const totalMin = log?.summary?.total_minutes || 0;
  const targetMin = 10 * 60; // 10 hour workday
  const pct = Math.min(100, Math.round((totalMin / targetMin) * 100));

  return (
    <div>
      {/* Filters */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
          <HiOutlineCalendar className="w-4 h-4 text-gray-400" />
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="text-sm bg-transparent focus:outline-none text-gray-700" />
        </div>
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
          <HiOutlineUser className="w-4 h-4 text-gray-400" />
          <select value={userId} onChange={e => setUserId(e.target.value)}
            className="text-sm bg-transparent focus:outline-none text-gray-700 pr-4">
            <option value="">All Facility Managers</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <button onClick={load} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl">
          <HiOutlineRefresh className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading work log...</div>
      ) : !log ? null : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Tasks Done',          value: log.summary.tasks_completed,    color: 'text-green-600',  bg: 'bg-green-50' },
              { label: 'Complaints Resolved', value: log.summary.complaints_resolved,color: 'text-orange-600', bg: 'bg-orange-50' },
              { label: 'Other Works',         value: log.summary.other_works,        color: 'text-violet-600', bg: 'bg-violet-50' },
              { label: 'Total Logged',        value: fmtMinutes(totalMin) || '0 min', color: 'text-blue-600',  bg: 'bg-blue-50' },
            ].map(c => (
              <div key={c.label} className={`${c.bg} rounded-xl p-4 border border-white`}>
                <p className={`text-2xl font-bold ${c.color}`}>{c.value}</p>
                <p className="text-xs text-gray-500 mt-0.5">{c.label}</p>
              </div>
            ))}
          </div>

          {/* Hours coverage bar */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 mb-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-700">Daily Hours Coverage (10h workday)</p>
              <p className="text-sm font-bold text-gray-800">{fmtMinutes(totalMin) || '0 min'} / 10h</p>
            </div>
            <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
                style={{ width: `${pct}%` }} />
            </div>
            <p className="text-xs text-gray-400 mt-1">{pct}% of working hours logged</p>
          </div>

          {/* Leave day banner (when a specific FM was on leave) */}
          {log.leave_days?.length > 0 && (
            <div className="mb-6 rounded-xl border border-amber-200 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border-b border-amber-200">
                <span className="text-amber-500 text-base">🏖️</span>
                <span className="text-sm font-semibold text-amber-700">On Leave / Off</span>
              </div>
              <div className="bg-white divide-y divide-gray-100">
                {log.leave_days.map((l, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-base flex-shrink-0 bg-amber-100">
                      {l.reason === 'leave' ? '🏖️' : l.reason === 'holiday' ? '🎉' : '😴'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800">{l.user_name}</p>
                      {l.notes && <p className="text-xs text-gray-400 italic">{l.notes}</p>}
                    </div>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 capitalize">
                      {l.reason === 'off' ? 'Day Off' : l.reason.charAt(0).toUpperCase() + l.reason.slice(1)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-user summary (if all users) */}
          {!userId && log.by_user?.length > 0 && (
            <div className="mb-6">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Per Staff Summary</p>
              <div className="space-y-2">
                {log.by_user.map((u, i) => (
                  <div key={i} className={`flex items-center gap-3 bg-white border rounded-xl px-4 py-3 ${u.on_leave ? 'border-amber-200 bg-amber-50' : 'border-gray-200'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${u.on_leave ? 'bg-amber-200 text-amber-700' : 'text-white'}`}
                      style={u.on_leave ? {} : { background: 'linear-gradient(135deg,#032841,#043959)' }}>
                      {u.on_leave
                        ? (u.leave_reason === 'leave' ? '🏖️' : u.leave_reason === 'holiday' ? '🎉' : '😴')
                        : (u.name?.charAt(0) || '?')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800">{u.name}</p>
                      {u.on_leave ? (
                        <p className="text-xs text-amber-600 font-medium">
                          {u.leave_reason === 'off' ? 'Day Off' : u.leave_reason === 'holiday' ? 'Holiday' : 'On Leave'}
                          {u.leave_notes ? ` · ${u.leave_notes}` : ''}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-400">
                          {u.tasks} tasks · {u.complaints} complaints · {u.works} works
                        </p>
                      )}
                    </div>
                    {u.on_leave ? (
                      <span className="text-xs font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-600">
                        Absent
                      </span>
                    ) : (
                      <p className="text-sm font-semibold text-blue-600 flex-shrink-0">
                        {fmtMinutes(u.minutes_logged) || '—'}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pending section — hidden when the selected FM was on leave */}
          {((log.pending?.tasks?.length > 0) || (log.pending?.complaints?.length > 0)) && !log.leave_days?.length && (
            <div className="mb-6 rounded-xl border border-red-200 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border-b border-red-200">
                <HiOutlineBell className="w-4 h-4 text-red-500" />
                <span className="text-sm font-semibold text-red-700">
                  Not Completed — {(log.pending.tasks?.length || 0) + (log.pending.complaints?.length || 0)} items pending
                </span>
              </div>
              <div className="bg-white divide-y divide-gray-100">
                {log.pending.tasks?.map(t => (
                  <div key={t.id} className="flex items-center gap-3 px-4 py-2.5">
                    <HiOutlineClipboardCheck className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="flex-1 text-sm text-gray-700">{t.title}</span>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                      t.priority === 'high'   ? 'bg-red-100 text-red-600' :
                      t.priority === 'medium' ? 'bg-amber-100 text-amber-600' :
                                                 'bg-gray-100 text-gray-500'
                    }`}>
                      {t.priority === 'high' ? '🔴' : t.priority === 'medium' ? '🟡' : '🟢'} {t.priority}
                    </span>
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Task</span>
                  </div>
                ))}
                {log.pending.complaints?.map(c => (
                  <div key={c.id} className="flex items-center gap-3 px-4 py-2.5">
                    <HiOutlineExclamationCircle className="w-4 h-4 text-orange-400 flex-shrink-0" />
                    <span className="flex-1 text-sm text-gray-700">{c.title}</span>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                      c.priority === 'high'   ? 'bg-red-100 text-red-600' :
                      c.priority === 'medium' ? 'bg-amber-100 text-amber-600' :
                                                 'bg-gray-100 text-gray-500'
                    }`}>
                      {c.priority === 'high' ? '🔴' : c.priority === 'medium' ? '🟡' : '🟢'} {c.priority}
                    </span>
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-600">Complaint</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timeline */}
          {log.timeline.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <HiOutlineChartBar className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No activity recorded for this date</p>
            </div>
          ) : (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Activity Timeline · {log.timeline.length} entries
              </p>
              <div className="relative">
                <div className="absolute left-[19px] top-0 bottom-0 w-px bg-gray-200" />
                <div className="space-y-3">
                  {log.timeline.map((entry, i) => {
                    const m = TYPE_META[entry.type];
                    return (
                      <div key={i} className="flex gap-4">
                        <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center z-10 ${m.dot} shadow-sm`}>
                          {entry.type === 'task'      && <HiOutlineClipboardCheck className="w-4 h-4 text-white" />}
                          {entry.type === 'complaint' && <HiOutlineExclamationCircle className="w-4 h-4 text-white" />}
                          {entry.type === 'work'      && <HiOutlineBriefcase className="w-4 h-4 text-white" />}
                        </div>
                        <div className={`flex-1 bg-white border border-l-4 ${m.border} border-gray-200 rounded-xl p-4 shadow-sm`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${m.badge}`}>{m.label}</span>
                                {entry.type === 'task' && entry.priority && (
                                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                                    entry.priority === 'high'   ? 'bg-red-100 text-red-600' :
                                    entry.priority === 'medium' ? 'bg-amber-100 text-amber-600' :
                                                                   'bg-gray-100 text-gray-500'
                                  }`}>
                                    {entry.priority === 'high' ? '🔴' : entry.priority === 'medium' ? '🟡' : '🟢'} {entry.priority.charAt(0).toUpperCase() + entry.priority.slice(1)}
                                  </span>
                                )}
                                {entry.time && (
                                  <span className="text-[11px] text-gray-400 flex items-center gap-1">
                                    <HiOutlineClock className="w-3 h-3" />
                                    {fmtTime(entry.time)}
                                  </span>
                                )}
                                {entry.user && (
                                  <span className="text-[11px] text-gray-400">by {entry.user}</span>
                                )}
                              </div>
                              <p className="font-semibold text-sm text-gray-800">{entry.title}</p>
                              {entry.description && (
                                <p className="text-xs text-gray-500 mt-0.5">{entry.description}</p>
                              )}
                              {entry.type === 'complaint' && (
                                <div className="mt-1.5 space-y-0.5">
                                  {entry.raised_by && (
                                    <p className="text-xs text-gray-400">
                                      Raised by {entry.raised_by}{entry.unit ? ` · ${entry.unit}` : ''}
                                    </p>
                                  )}
                                  {entry.notes && (
                                    <p className="text-xs text-gray-600 italic">"{entry.notes}"</p>
                                  )}
                                </div>
                              )}
                              {(entry.type === 'task' || entry.type === 'work') && entry.notes && (
                                <p className="text-xs text-gray-500 mt-1 italic">"{entry.notes}"</p>
                              )}
                              {entry.type === 'work' && entry.est_time && (
                                <p className="text-xs text-gray-400 mt-0.5">Est: {entry.est_time}</p>
                              )}
                            </div>
                            <div className="text-right flex-shrink-0">
                              {entry.time_spent ? (
                                <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5 text-center">
                                  <p className="text-sm font-bold text-blue-700">{fmtMinutes(entry.time_spent)}</p>
                                  <p className="text-[10px] text-blue-400">logged</p>
                                </div>
                              ) : (
                                <span className="text-[11px] text-gray-300 italic">no time logged</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const PRIORITY_FILTERS = [
  { key: '',       label: 'All' },
  { key: 'high',   label: '🔴 High' },
  { key: 'medium', label: '🟡 Medium' },
  { key: 'low',    label: '🟢 Low' },
];

function AdminTasks() {
  const [tasks, setTasks]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [taskModal, setTaskModal]       = useState(null);
  const [priorityFilter, setPriorityFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { setTasks(await getFacilityTasks()); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    await deleteFacilityTask(id);
    setTasks(ts => ts.filter(t => t.id !== id));
  };

  const filteredTasks = priorityFilter
    ? tasks.filter(t => t.priority === priorityFilter)
    : tasks;

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-1.5 flex-wrap">
          {PRIORITY_FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setPriorityFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                priorityFilter === f.key
                  ? 'bg-gray-800 text-white border-gray-800'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl">
            <HiOutlineRefresh className="w-4 h-4" />
          </button>
          <Btn onClick={() => setTaskModal('add')}>
            <span className="flex items-center gap-1.5"><HiOutlinePlus className="w-4 h-4" /> Add Task</span>
          </Btn>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading tasks...</div>
      ) : filteredTasks.length === 0 ? (
        <div className="text-center py-14 text-gray-400">
          <HiOutlineClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">{tasks.length === 0 ? 'No tasks configured yet' : 'No tasks match the selected priority'}</p>
          {tasks.length === 0 && <p className="text-xs mt-1">Add tasks that the facility manager should complete every day</p>}
        </div>
      ) : (
        <div className="space-y-2.5">
          {filteredTasks.map(task => (
            <TaskCard key={task.id} task={task} readOnly
              canManage onEdit={t => setTaskModal(t)} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {taskModal && (
        <TaskFormModal
          task={taskModal === 'add' ? null : taskModal}
          onClose={() => setTaskModal(null)}
          onSaved={saved => {
            setTasks(ts => {
              const exists = ts.find(t => t.id === saved.id);
              return exists ? ts.map(t => t.id === saved.id ? saved : t) : [saved, ...ts];
            });
            setTaskModal(null);
          }} />
      )}
    </div>
  );
}

function AdminComplaints() {
  const { user } = useAuth();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { setComplaints(await getFacilityComplaints(filter || undefined)); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const counts = {
    '':         complaints.length,
    open:       complaints.filter(c => c.status === 'open').length,
    finished:   complaints.filter(c => c.status === 'finished').length,
    resolved:   complaints.filter(c => c.status === 'resolved').length,
  };

  const filters = [
    { key: '',         label: 'All',      bg: 'bg-gray-100 text-gray-700',        active: 'bg-gray-800 text-white' },
    { key: 'open',     label: 'Open',     bg: 'bg-red-100 text-red-700',          active: 'bg-red-600 text-white' },
    { key: 'finished', label: 'Finished', bg: 'bg-blue-100 text-blue-700',        active: 'bg-blue-600 text-white' },
    { key: 'resolved', label: 'Resolved', bg: 'bg-green-100 text-green-700',      active: 'bg-green-600 text-white' },
  ];

  return (
    <div>
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {filters.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${filter === f.key ? f.active : f.bg}`}>
            {f.label}
            <span className="ml-1.5 opacity-70">({counts[f.key]})</span>
          </button>
        ))}
        <button onClick={load} className="ml-auto p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
          <HiOutlineRefresh className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading complaints...</div>
      ) : complaints.length === 0 ? (
        <div className="text-center py-14 text-gray-400">
          <HiOutlineExclamationCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No {filter ? `"${filter}"` : ''} complaints</p>
        </div>
      ) : (
        <div className="space-y-3">
          {complaints.map(c => (
            <ComplaintCard key={c.id} complaint={c} userRole={user?.role}
              onUpdate={updated => setComplaints(cs => cs.map(x => x.id === updated.id ? updated : x))} />
          ))}
        </div>
      )}
    </div>
  );
}

function AdminOtherWorks() {
  const [works, setWorks]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().slice(0, 10));

  const load = useCallback(async () => {
    setLoading(true);
    try { setWorks(await getFacilityOtherWorks()); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = dateFilter
    ? works.filter(w => w.created_at?.slice(0, 10) === dateFilter)
    : works;

  return (
    <div>
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <p className="text-sm text-gray-500">
          {dateFilter
            ? `${filtered.length} work${filtered.length !== 1 ? 's' : ''} on ${fmtDate(dateFilter)}`
            : `${works.length} work${works.length !== 1 ? 's' : ''} logged`}
        </p>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={dateFilter}
            onChange={e => setDateFilter(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-violet-400 text-gray-600"
          />
          {dateFilter && (
            <button onClick={() => setDateFilter('')}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
              <HiOutlineX className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={load} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl">
            <HiOutlineRefresh className="w-4 h-4" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-14 text-gray-400">
          <HiOutlineBriefcase className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">{dateFilter ? 'No works logged on this date' : 'No other works logged yet'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(w => (
            <div key={w.id} className="p-4 rounded-xl border bg-white border-gray-200">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0 bg-violet-400" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-gray-800">{w.title}</p>
                  {w.description && <p className="text-xs text-gray-500 mt-0.5">{w.description}</p>}
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <HiOutlineUser className="w-3 h-3" />{w.added_by_user?.name}
                    </span>
                    {w.est_time && (
                      <span className="flex items-center gap-1">
                        <HiOutlineClock className="w-3 h-3" />Est: {w.est_time}
                      </span>
                    )}
                    {w.time_spent && (
                      <span className="font-semibold text-blue-600">
                        Spent: {fmtMinutes(w.time_spent)}
                      </span>
                    )}
                    <span>{fmtDate(w.created_at)}</span>
                  </div>
                  {w.notes && <p className="text-xs text-gray-500 mt-1 italic">"{w.notes}"</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── PENDING ALERTS BANNER ───────────────────────────────────────────────────

function PendingAlertsBanner({ onNavigate }) {
  const [alerts, setAlerts]       = useState(null); // null = loading
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yd = yesterday.toISOString().slice(0, 10);
    const todayStr = new Date().toISOString().slice(0, 10);

    Promise.all([
      getFacilityComplaints(),
      getFacilityTasks(),
      getFacilityWorkLog(yd),
    ]).then(([complaints, tasks, log]) => {
      // Overdue complaints: open/in_progress created before today
      const overdueComplaints = complaints.filter(c =>
        c.status !== 'resolved' && c.created_at && c.created_at.slice(0, 10) < todayStr
      );

      // Yesterday's missed tasks: active tasks with no completion yesterday
      const completedYesterday = new Set(
        (log.timeline || []).filter(e => e.type === 'task').map(e => e.task_id)
      );
      const missedTasks = tasks.filter(t => t.is_active && !completedYesterday.has(t.id));

      setAlerts({ overdueComplaints, missedTasks });
    }).catch(() => setAlerts({ overdueComplaints: [], missedTasks: [] }));
  }, []);

  if (dismissed || alerts === null) return null;
  const { overdueComplaints, missedTasks } = alerts;
  if (overdueComplaints.length === 0 && missedTasks.length === 0) return null;

  const total = overdueComplaints.length + missedTasks.length;

  return (
    <div className="mb-5 rounded-2xl border border-red-200 overflow-hidden shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3"
        style={{ background: 'linear-gradient(135deg,#7f1d1d,#991b1b)' }}>
        <div className="flex items-center gap-2 text-white">
          <HiOutlineBell className="w-5 h-5 animate-pulse" />
          <span className="font-semibold text-sm">
            {total} Pending Item{total !== 1 ? 's' : ''} Require Attention
          </span>
          <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
            From previous day{total !== 1 ? 's' : ''}
          </span>
        </div>
        <button onClick={() => setDismissed(true)}
          className="text-white/70 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-all">
          <HiOutlineX className="w-4 h-4" />
        </button>
      </div>

      {/* Alert rows */}
      <div className="bg-red-50 divide-y divide-red-100">
        {overdueComplaints.length > 0 && (
          <button onClick={() => onNavigate('complaints')}
            className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-red-100 transition-all text-left group">
            <div className="w-9 h-9 bg-red-100 border border-red-200 rounded-xl flex items-center justify-center flex-shrink-0">
              <HiOutlineExclamationCircle className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-800">
                {overdueComplaints.length} Overdue Complaint{overdueComplaints.length !== 1 ? 's' : ''}
              </p>
              <p className="text-xs text-red-600 mt-0.5 truncate">
                {overdueComplaints.slice(0, 2).map(c => c.title).join(' · ')}
                {overdueComplaints.length > 2 && ` +${overdueComplaints.length - 2} more`}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs font-semibold text-white bg-red-500 px-2.5 py-1 rounded-full">
                High Priority
              </span>
              <HiOutlineChevronRight className="w-4 h-4 text-red-400 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </button>
        )}

        {missedTasks.length > 0 && (
          <button onClick={() => onNavigate('tasks')}
            className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-red-100 transition-all text-left group">
            <div className="w-9 h-9 bg-amber-100 border border-amber-200 rounded-xl flex items-center justify-center flex-shrink-0">
              <HiOutlineClipboardCheck className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-800">
                {missedTasks.length} Task{missedTasks.length !== 1 ? 's' : ''} Not Completed Yesterday
              </p>
              <p className="text-xs text-amber-600 mt-0.5 truncate">
                {missedTasks.slice(0, 2).map(t => t.title).join(' · ')}
                {missedTasks.length > 2 && ` +${missedTasks.length - 2} more`}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs font-semibold text-white bg-amber-500 px-2.5 py-1 rounded-full">
                Follow Up
              </span>
              <HiOutlineChevronRight className="w-4 h-4 text-amber-400 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </button>
        )}
      </div>
    </div>
  );
}

function AdminView() {
  const [tab, setTab] = useState('tasks');

  return (
    <div>
      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-6 w-fit">
        {ADMIN_TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
              tab === key ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
        {tab === 'tasks'           && <AdminTasks />}
        {tab === 'complaints'      && <AdminComplaints />}
        {tab === 'other-works'     && <AdminOtherWorks />}
        {tab === 'work-log'        && <WorkLogTab />}
        {tab === 'monthly-summary' && <MonthlySummaryTab />}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE ROOT
// ═══════════════════════════════════════════════════════════════════════════

export default function FacilityCompliancePage() {
  const { user } = useAuth();
  const isCaretaker = user?.role === 'caretaker';

  return (
    <AppLayout title="Facility Management" breadcrumbs="Facility Compliance">
      <div style={{ maxWidth: isCaretaker ? 900 : 1100, margin: '0 auto', padding: '24px 24px' }}>
        {isCaretaker ? <FMDashboard user={user} /> : <AdminView />}
      </div>
    </AppLayout>
  );
}
